import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { FormType } from '../types';

const DEFAULT_TEST_ID = 'duplicate-form-modal';
const MAX_TITLE_LENGTH = 120;

const getTrimmedLength = (value: string): number => value.trim().length;

type DuplicateMode = 'all-languages' | 'default-only';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (mode: DuplicateMode, newTitle: string) => void;
  isLoading?: boolean;
  formType: FormType;
  'data-testid'?: string;
}

const DUPLICATE_OPTIONS: {
  value: DuplicateMode;
  title: string;
  description: string;
}[] = [
  {
    value: 'all-languages',
    title: 'Duplicate all language variants',
    description: 'Creates a new copy of this form in every available language.',
  },
  {
    value: 'default-only',
    title: 'Duplicate default language only',
    description:
      'Creates a new copy of this form in the default language. Existing translations are not duplicated.',
  },
];

export const DuplicateFormModal = ({
  open,
  setOpen,
  onConfirm,
  isLoading = false,
  formType: _formType,
  'data-testid': dataTestId = DEFAULT_TEST_ID,
}: Props) => {
  const theme = useTheme();
  const [duplicateMode, setDuplicateMode] =
    useState<DuplicateMode>('default-only');
  const [newFormTitle, setNewFormTitle] = useState('');

  const titleLength = getTrimmedLength(newFormTitle);
  const isTitleTooLong = titleLength > MAX_TITLE_LENGTH;
  const isConfirmDisabled = isLoading || titleLength === 0 || isTitleTooLong;

  const handleClose = useCallback(() => {
    if (isLoading) return;
    setOpen(false);
    setDuplicateMode('default-only');
    setNewFormTitle('');
  }, [isLoading, setOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm(duplicateMode, newFormTitle.trim());
  }, [onConfirm, duplicateMode, newFormTitle]);

  useEffect(() => {
    if (!open) {
      setDuplicateMode('default-only');
      setNewFormTitle('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="duplicate-form-modal-title"
      aria-describedby="duplicate-form-modal-description"
      data-testid={dataTestId}
    >
      <DialogTitle component="div">
        <Typography variant="h6" component="h2" id="duplicate-form-modal-title">
          Duplicate form
        </Typography>
        <Typography variant="body2" component="p">
          Select duplication setting
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={isLoading}
          size="small"
          color="inherit"
          data-testid={`${dataTestId}-close`}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent id="duplicate-form-modal-description">
        <Box
          role="radiogroup"
          aria-label="Duplication setting"
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {DUPLICATE_OPTIONS.map((opt) => {
            const isSelected = duplicateMode === opt.value;
            return (
              <Box
                key={opt.value}
                role="radio"
                aria-checked={isSelected}
                aria-label={opt.title}
                tabIndex={isLoading ? -1 : 0}
                onClick={() => !isLoading && setDuplicateMode(opt.value)}
                onKeyDown={(e) => {
                  if (!isLoading && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setDuplicateMode(opt.value);
                  }
                }}
                data-testid={`${dataTestId}-${opt.value}`}
                sx={{
                  border: `${isSelected ? '2px' : '1px'} solid ${
                    isSelected
                      ? theme.atlas.semantic.color.type.default
                      : theme.atlas.semantic.color.outline.default
                  }`,
                  borderRadius: '8px',
                  padding: 2,
                  cursor: isLoading ? 'default' : 'pointer',
                  outline: 'none',
                  '&:focus-visible': {
                    boxShadow: `0 0 0 2px ${theme.atlas.semantic.color.ui.focusRing}`,
                  },
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {opt.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              mb: 0.5,
            }}
          >
            <Typography
              variant="body2"
              component="label"
              htmlFor="new-form-title"
            >
              Add new form title <span aria-label="required">*</span>
            </Typography>
            <Typography
              variant="caption"
              color={isTitleTooLong ? 'error' : 'text.secondary'}
            >
              {titleLength}/{MAX_TITLE_LENGTH}
            </Typography>
          </Box>
          <TextField
            id="new-form-title"
            fullWidth
            placeholder="Enter form title"
            value={newFormTitle}
            onChange={(e) => setNewFormTitle(e.target.value)}
            disabled={isLoading}
            error={isTitleTooLong}
            data-testid={`${dataTestId}-title-input`}
            helperText={
              isTitleTooLong ? 'Title cannot exceed 120 characters' : undefined
            }
            slotProps={{
              formHelperText: {
                role: 'alert',
              } as Record<string, unknown>,
            }}
          />
        </>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          data-testid={`${dataTestId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          data-testid={`${dataTestId}-confirm`}
          autoFocus
        >
          {isLoading ? 'Duplicating...' : 'Duplicate form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
